import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { AREA_TEXTS, BODY_FIGURE_TEXTS, AGE_RANGE_TEXTS, HEIGHT_RANGE_TEXTS } from '../../consts';
import { EXAMPLE_CATS, pickRandomItems } from './forumExampleCats';

type FormValues = {
  area: string;
  bloodType: string[];
  ageRange: string[];
  bodyFigure: string[];
  heightRange: string[];
  description: string;
  expiresAt: string;
};

type Props = {
  title: string;
  initialValues?: Partial<FormValues>;
  loading: boolean;
  onSubmit: (body: Record<string, string | string[] | undefined | null>, status: 'DRAFT' | 'OPEN') => void;
};

const MAX_DESCRIPTION = 1500;

const ForumPostForm = ({ title, initialValues, loading, onSubmit }: Props) => {
  const [ area, setArea ] = useState('');
  const [ bloodType, setBloodType ] = useState<string[]>([]);
  const [ ageRange, setAgeRange ] = useState<string[]>([]);
  const [ bodyFigure, setBodyFigure ] = useState<string[]>([]);
  const [ heightRange, setHeightRange ] = useState<string[]>([]);
  const [ description, setDescription ] = useState('');
  const [ expiresAt, setExpiresAt ] = useState('');
  const [ helperOpen, setHelperOpen ] = useState(false);
  const [ activeCat, setActiveCat ] = useState('intro');
  const [ sheetOpen, setSheetOpen ] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const caretRef = useRef(0);
  const displayedCats = useMemo(
    () => EXAMPLE_CATS.map(cat => ({ ...cat, items: pickRandomItems(cat.items, 4) })),
    []
  );

  useEffect(() => {
    if (!initialValues) return;
    setArea(initialValues.area || '');
    setBloodType(initialValues.bloodType || []);
    setAgeRange(initialValues.ageRange || []);
    setBodyFigure(initialValues.bodyFigure || []);
    setHeightRange(initialValues.heightRange || []);
    setDescription(initialValues.description || '');
    setExpiresAt(initialValues.expiresAt || '');
  }, [ initialValues ]);

  const toggle = (current: string[], value: string, setter: (v: string[]) => void) => {
    setter(current.includes(value) ? current.filter(v => v !== value) : [ ...current, value ]);
  };

  const rememberCaret = () => {
    if (taRef.current) caretRef.current = taRef.current.selectionStart;
  };

  const insertExample = (snippet: string) => {
    const pos = Math.min(caretRef.current ?? description.length, description.length);
    const before = description.slice(0, pos);
    const after = description.slice(pos);
    let piece = snippet;
    if (before && !before.endsWith('\n')) piece = '\n' + piece;
    if (after && !after.startsWith('\n')) piece = piece + '\n';
    const next = (before + piece + after).slice(0, MAX_DESCRIPTION);
    const newPos = Math.min((before + piece).length, MAX_DESCRIPTION);
    setDescription(next);
    caretRef.current = newPos;
    requestAnimationFrame(() => {
      if (taRef.current) {
        taRef.current.focus();
        taRef.current.setSelectionRange(newPos, newPos);
      }
    });
  };

  const openSheet = () => {
    if (taRef.current) caretRef.current = taRef.current.selectionStart;
    setSheetOpen(true);
  };

  const insertExampleMobile = (snippet: string) => {
    insertExample(snippet);
    setSheetOpen(false);
  };

  const activeCatData = displayedCats.find(c => c.id === activeCat) || displayedCats[0];

  const handleSubmit = (status: 'DRAFT' | 'OPEN') => {
    if (status === 'OPEN') {
      if (!area) { toast.error('提供エリアを選択してください'); return; }
      if (!description.trim()) { toast.error('コメントを入力してください'); return; }
      if (!expiresAt) { toast.error('募集終了日を選択してください'); return; }
    }

    if (expiresAt && expiresAt > maxDate) { toast.error('募集終了日は最大2週間以内に設定してください'); return; }

    if (status === 'OPEN' && !window.confirm('投稿してよろしいですか？')) return;

    onSubmit({
      area: area || undefined,
      blood_type: bloodType.length ? bloodType : undefined,
      age_range: ageRange.length ? ageRange : undefined,
      body_figure: bodyFigure.length ? bodyFigure : undefined,
      height_range: heightRange.length ? heightRange : undefined,
      description: description || undefined,
      expires_at: expiresAt || null,
    }, status);
  };

  const minDate = new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  return (
    <section className="donee_forum pink">
      <h2 className="main_title"><span>{ title }</span></h2>
      <div className="form">
        <div className="guide-inline">
          <h3>投稿前にご確認ください。</h3>
          <ul>
            <li>連絡先（電話番号、SNSアカウント、メールアドレス等）は本文に直接記載せず、ご応募いただいたドナー様とのメッセージ機能でやり取りください。</li>
            <li>個人を特定できる情報（氏名・住所等）は記載しないでください。</li>
            <li>募集条件に合わないドナー様からご応募いただく場合もあります。応募者の詳細はドナープロフィールでご確認ください。</li>
          </ul>
        </div>
        <form>

          <div className="form-section">
            <h3 className="form-section-title">
              コメント <span className="required">必須</span>
            </h3>
            <textarea
              ref={ taRef }
              value={ description }
              onChange={ e => { setDescription(e.target.value.slice(0, MAX_DESCRIPTION)); rememberCaret(); } }
              onSelect={ rememberCaret }
              onClick={ rememberCaret }
              onKeyUp={ rememberCaret }
              rows={ 5 }
              maxLength={ MAX_DESCRIPTION }
              placeholder="ご自身の状況、希望される提供方法、活動期間など、ドナー様にお伝えしたい内容を具体的にお書きください。（1500文字以内）"
            />
            <small>{ description.length }/{ MAX_DESCRIPTION }</small>

            {/* デスクトップ: インライン折りたたみヘルパー */}
            <div className="ex-helper">
              <button
                type="button"
                className={ `ex-toggle${ helperOpen ? ' is-open' : '' }` }
                aria-expanded={ helperOpen }
                onClick={ () => setHelperOpen(v => !v) }
              >
                <span className="ex-pen">✎</span>
                文章を書くのが難しいときは、例文から選んで挿入できます
                <span className="ex-caret"></span>
              </button>

              { helperOpen && (
                <div className="ex-panel">
                  <p className="ex-lead">
                    お伝えしたい項目を選ぶと、例文をカーソル位置に挿入します。挿入後は<strong>ご自身の言葉に書き換えて</strong>お使いください。
                  </p>
                  <div className="ex-tabs" role="tablist">
                    { displayedCats.map(c => (
                      <button
                        key={ c.id }
                        type="button"
                        role="tab"
                        aria-selected={ activeCat === c.id }
                        className={ activeCat === c.id ? 'is-active' : '' }
                        onClick={ () => setActiveCat(c.id) }
                      >
                        { c.label }
                      </button>
                    )) }
                  </div>
                  <ul className="ex-list" key={ activeCat }>
                    { activeCatData.items.map((t, i) => (
                      <li className="ex-item" key={ i }>
                        <p className="ex-text">{ t }</p>
                        <button type="button" className="ex-insert" onClick={ () => insertExample(t) }>
                          ＋ 挿入
                        </button>
                      </li>
                    )) }
                  </ul>
                </div>
              ) }
            </div>

            {/* モバイル: ボトムシートトリガー */}
            <div className="ex-helper-mobile">
              <button
                type="button"
                className="m-assist"
                onClick={ openSheet }
              >
                <span className="m-assist-pen">✎</span>
                例文から選んで挿入する
              </button>
              <p className="m-help">書き出しに迷ったら、項目別の例文を呼び出せます。</p>
            </div>

            {/* モバイル: ボトムシート */}
            { sheetOpen && (
              <div className="m-sheet-wrap">
                <div className="m-backdrop" onClick={ () => setSheetOpen(false) }></div>
                <div className="m-sheet" role="dialog" aria-label="例文を挿入">
                  <div className="m-sheet-grip"></div>
                  <div className="m-sheet-head">
                    <div className="m-sheet-title">例文を挿入</div>
                    <button
                      type="button"
                      className="m-sheet-close"
                      aria-label="閉じる"
                      onClick={ () => setSheetOpen(false) }
                    >
                      ×
                    </button>
                  </div>
                  <p className="m-sheet-lead">
                    項目を選び、使いたい例文をタップ。挿入後は<strong>ご自身の言葉に書き換えて</strong>ください。
                  </p>
                  <div className="m-sheet-tabs">
                    { displayedCats.map(c => (
                      <button
                        key={ c.id }
                        type="button"
                        className={ activeCat === c.id ? 'is-active' : '' }
                        onClick={ () => setActiveCat(c.id) }
                      >
                        { c.label }
                      </button>
                    )) }
                  </div>
                  <ul className="m-sheet-list" key={ activeCat }>
                    { activeCatData.items.map((t, i) => (
                      <li className="m-sheet-item" key={ i } onClick={ () => insertExampleMobile(t) }>
                        <p className="m-sheet-text">{ t }</p>
                        <span className="m-sheet-insert">＋ 挿入</span>
                      </li>
                    )) }
                  </ul>
                </div>
              </div>
            ) }
          </div>

          <div className="form-section">
            <h3 className="form-section-title">希望するドナーの条件</h3>
            <p className="form-section-note">
              ご自身の情報ではなく、募集したいドナー様に希望する条件をお選びください。
            </p>
            <dl className="graf_list pink">
              <dt>提供エリア <span className="required">必須</span></dt>
              <dd>
                <div className="select select01">
                  <select value={ area } onChange={ e => setArea(e.target.value) }>
                    <option value="">選択してください</option>
                    { Object.entries(AREA_TEXTS).map(([ k, v ]) => (
                      <option key={ k } value={ k }>{ v }</option>
                    )) }
                  </select>
                </div>
              </dd>

              <dt>血液型</dt>
              <dd>
                <div className="tag_group">
                  { ['A', 'B', 'AB', 'O'].map(bt => (
                    <label key={ bt } className="tag_label">
                      <input type="checkbox" value={ bt } checked={ bloodType.includes(bt) }
                        onChange={ () => toggle(bloodType, bt, setBloodType) } />
                      { bt }
                    </label>
                  )) }
                </div>
              </dd>

              <dt>年齢</dt>
              <dd>
                <div className="tag_group">
                  { Object.entries(AGE_RANGE_TEXTS).map(([ k, v ]) => (
                    <label key={ k } className="tag_label">
                      <input type="checkbox" value={ k } checked={ ageRange.includes(k) }
                        onChange={ () => toggle(ageRange, k, setAgeRange) } />
                      { v }
                    </label>
                  )) }
                </div>
              </dd>

              <dt>体型</dt>
              <dd>
                <div className="tag_group">
                  { Object.entries(BODY_FIGURE_TEXTS).map(([ k, v ]) => (
                    <label key={ k } className="tag_label">
                      <input type="checkbox" value={ k } checked={ bodyFigure.includes(k) }
                        onChange={ () => toggle(bodyFigure, k, setBodyFigure) } />
                      { v }
                    </label>
                  )) }
                </div>
              </dd>

              <dt>身長</dt>
              <dd>
                <div className="tag_group">
                  { Object.entries(HEIGHT_RANGE_TEXTS).map(([ k, v ]) => (
                    <label key={ k } className="tag_label">
                      <input type="checkbox" value={ k } checked={ heightRange.includes(k) }
                        onChange={ () => toggle(heightRange, k, setHeightRange) } />
                      { v }
                    </label>
                  )) }
                </div>
              </dd>
            </dl>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">
              募集終了日 <span className="required">必須</span>
            </h3>
            <input type="date" value={ expiresAt }
              onChange={ e => setExpiresAt(e.target.value) } min={ minDate } max={ maxDate } />
            <small>最大2週間</small>
          </div>

          <div className="form_actions">
            <button type="button" className="w_pinkbtn" onClick={ () => handleSubmit('DRAFT') } disabled={ loading }>
              下書き保存
            </button>
            <button type="button" className="pinkbtn" onClick={ () => handleSubmit('OPEN') } disabled={ loading }>
              投稿する
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ForumPostForm;
